// src/services/survey.service.ts
import { db } from '../config/firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import {
  Survey,
  SurveyStatus,
  SurveyQuestion,
  CreateSurveyDto,
  UpdateSurveyDto,
  SurveySettings,
  ConditionalLogicRule,
  SkipPattern,
} from '../types/survey.types';

const COLLECTION_NAME = 'surveys';


export async function createSurvey(
  data: CreateSurveyDto,
  userId: string,
  userName: string
): Promise<Survey> {
  const now = Timestamp.now();

  const surveyData = {
    projectId: data.projectId,
    name: data.name,
    description: data.description || null,
    version: 1,
    status: 'draft' as SurveyStatus,
    questions: data.questions || [],
    conditionalLogic: [],
    skipPatterns: [],
    settings: {
      allowBackNavigation: true,
      showProgressBar: true,
      shuffleQuestions: false,
      shuffleOptions: false,
      requireAllQuestions: false,
      showQuestionNumbers: true,
      submitButtonText: 'Submit',
      thankYouMessage: 'Thank you for completing this survey!',
    } as SurveySettings,
    createdBy: userId,
    createdByName: userName,
    createdAt: now,
    updatedAt: now,
    publishedAt: null,
    archivedAt: null,
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), surveyData);

  return {
    id: docRef.id,
    ...surveyData,
  } as Survey;
}

export async function duplicateSurvey(
  surveyId: string,
  newName: string,
  userId: string,
  userName: string
): Promise<Survey> {
  const original = await getSurvey(surveyId);
  if (!original) {
    throw new Error('Survey not found');
  }

  const now = Timestamp.now();

  const duplicateData = {
    projectId: original.projectId,
    name: newName,
    description: original.description,
    version: 1,
    status: 'draft' as SurveyStatus,
    questions: original.questions.map((q) => ({
      ...q,
      id: generateQuestionId(),
    })),
    conditionalLogic: original.conditionalLogic,
    skipPatterns: original.skipPatterns,
    settings: original.settings,
    createdBy: userId,
    createdByName: userName,
    createdAt: now,
    updatedAt: now,
    publishedAt: null,
    archivedAt: null,
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), duplicateData);

  return {
    id: docRef.id,
    ...duplicateData,
  } as Survey;
}


export async function getSurvey(surveyId: string): Promise<Survey | null> {
  const docRef = doc(db, COLLECTION_NAME, surveyId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as Survey;
}

export async function getProjectSurveys(projectId: string): Promise<Survey[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('projectId', '==', projectId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Survey[];
}

export async function getActiveSurveys(projectId: string): Promise<Survey[]> {
  // Get both active and draft surveys (exclude archived)
  const q = query(
    collection(db, COLLECTION_NAME),
    where('projectId', '==', projectId)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs
    .map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }) as Survey)
    .filter(s => s.status !== 'archived')
    .sort((a, b) => {
      // Sort active first, then by name
      if (a.status === 'active' && b.status !== 'active') return -1;
      if (a.status !== 'active' && b.status === 'active') return 1;
      return a.name.localeCompare(b.name);
    });
}

export async function getAllActiveSurveys(): Promise<Survey[]> {
  // Get all surveys regardless of project (for cases where projectId doesn't match)
  const q = query(
    collection(db, COLLECTION_NAME),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs
    .map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }) as Survey)
    .filter(s => s.status !== 'archived')
    .sort((a, b) => {
      // Sort active first, then by name
      if (a.status === 'active' && b.status !== 'active') return -1;
      if (a.status !== 'active' && b.status === 'active') return 1;
      return a.name.localeCompare(b.name);
    });
}


export async function updateSurvey(
  surveyId: string,
  data: UpdateSurveyDto
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, surveyId);

  // Check if name is being updated
  const nameChanging = data.name !== undefined;

  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
  });

  // If name changed, sync to all workloads that have this survey assigned
  if (nameChanging && data.name) {
    await syncSurveyNameToWorkloads(surveyId, data.name);
  }
}

/**
 * Sync survey name to all workloads that have this survey assigned
 */
async function syncSurveyNameToWorkloads(surveyId: string, newName: string): Promise<void> {
  try {
    const workloadsRef = collection(db, 'workloads');
    const q = query(workloadsRef, where('surveyId', '==', surveyId));
    const snapshot = await getDocs(q);

    if (snapshot.empty) return;

    const batch = writeBatch(db);
    snapshot.docs.forEach((docSnap) => {
      batch.update(docSnap.ref, {
        surveyName: newName,
        updatedAt: Timestamp.now(),
      });
    });

    await batch.commit();
  } catch (error) {
    console.error('Error syncing survey name to workloads:', error);
    // Don't throw - this is a background sync, survey update should still succeed
  }
}

export async function updateSurveyQuestions(
  surveyId: string,
  questions: SurveyQuestion[]
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, surveyId);

  await updateDoc(docRef, {
    questions,
    updatedAt: Timestamp.now(),
  });
}

export async function updateSurveySettings(
  surveyId: string,
  settings: Partial<SurveySettings>
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, surveyId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    throw new Error('Survey not found');
  }

  const currentSettings = existing.data().settings || {};

  await updateDoc(docRef, {
    settings: { ...currentSettings, ...settings },
    updatedAt: Timestamp.now(),
  });
}

export async function updateConditionalLogic(
  surveyId: string,
  rules: ConditionalLogicRule[]
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, surveyId);

  await updateDoc(docRef, {
    conditionalLogic: rules,
    updatedAt: Timestamp.now(),
  });
}

export async function updateSkipPatterns(
  surveyId: string,
  patterns: SkipPattern[]
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, surveyId);

  await updateDoc(docRef, {
    skipPatterns: patterns,
    updatedAt: Timestamp.now(),
  });
}


export async function publishSurvey(surveyId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, surveyId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    throw new Error('Survey not found');
  }

  const data = existing.data();
  const newVersion = (data.version || 1) + (data.status === 'active' ? 1 : 0);

  await updateDoc(docRef, {
    status: 'active',
    version: newVersion,
    publishedAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
}

export async function unpublishSurvey(surveyId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, surveyId);

  await updateDoc(docRef, {
    status: 'draft',
    updatedAt: Timestamp.now(),
  });
}

export async function archiveSurvey(surveyId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, surveyId);

  await updateDoc(docRef, {
    status: 'archived',
    archivedAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
}

export async function restoreSurvey(surveyId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, surveyId);

  await updateDoc(docRef, {
    status: 'draft',
    archivedAt: null,
    updatedAt: Timestamp.now(),
  });
}

export async function updateSurveyStatus(
  surveyId: string,
  status: SurveyStatus
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, surveyId);
  const now = Timestamp.now();

  const updateData: any = {
    status,
    updatedAt: now,
  };

  // Set appropriate timestamps based on status
  if (status === 'active') {
    updateData.publishedAt = now;
  } else if (status === 'archived') {
    updateData.archivedAt = now;
  }

  await updateDoc(docRef, updateData);
}


export async function addQuestion(
  surveyId: string,
  question: Omit<SurveyQuestion, 'id'>
): Promise<SurveyQuestion> {
  const docRef = doc(db, COLLECTION_NAME, surveyId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    throw new Error('Survey not found');
  }

  const questions = existing.data().questions || [];
  const newQuestion: SurveyQuestion = {
    ...question,
    id: generateQuestionId(),
    order: questions.length,
  };

  await updateDoc(docRef, {
    questions: [...questions, newQuestion],
    updatedAt: Timestamp.now(),
  });

  return newQuestion;
}

export async function updateQuestion(
  surveyId: string,
  questionId: string,
  updates: Partial<SurveyQuestion>
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, surveyId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    throw new Error('Survey not found');
  }

  const questions = existing.data().questions || [];
  const updatedQuestions = questions.map((q: SurveyQuestion) =>
    q.id === questionId ? { ...q, ...updates } : q
  );

  await updateDoc(docRef, {
    questions: updatedQuestions,
    updatedAt: Timestamp.now(),
  });
}

export async function deleteQuestion(
  surveyId: string,
  questionId: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, surveyId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    throw new Error('Survey not found');
  }

  const questions = existing.data().questions || [];
  const filteredQuestions = questions
    .filter((q: SurveyQuestion) => q.id !== questionId)
    .map((q: SurveyQuestion, index: number) => ({ ...q, order: index }));

  // Also remove any conditional logic referencing this question
  const conditionalLogic = (existing.data().conditionalLogic || []).filter(
    (rule: ConditionalLogicRule) =>
      !rule.targetQuestionIds.includes(questionId) &&
      !rule.conditions.some((c) => c.questionId === questionId)
  );

  await updateDoc(docRef, {
    questions: filteredQuestions,
    conditionalLogic,
    updatedAt: Timestamp.now(),
  });
}

export async function reorderQuestions(
  surveyId: string,
  questionIds: string[]
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, surveyId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    throw new Error('Survey not found');
  }

  const questions = existing.data().questions || [];
  const questionMap = new Map(questions.map((q: SurveyQuestion) => [q.id, q]));

  const reorderedQuestions = questionIds
    .map((id, index) => {
      const question = questionMap.get(id);
      return question ? { ...question, order: index } : null;
    })
    .filter(Boolean);

  await updateDoc(docRef, {
    questions: reorderedQuestions,
    updatedAt: Timestamp.now(),
  });
}


export async function deleteSurvey(surveyId: string): Promise<{ responsesDeleted: number }> {
  let responsesDeleted = 0;

  // Delete all survey responses for this survey
  const responsesQuery = query(
    collection(db, 'surveyResponses'),
    where('surveyId', '==', surveyId)
  );
  const responsesSnapshot = await getDocs(responsesQuery);

  if (responsesSnapshot.docs.length > 0) {
    // Batch delete in chunks of 450 (under Firestore's 500 limit)
    const chunks = [];
    for (let i = 0; i < responsesSnapshot.docs.length; i += 450) {
      chunks.push(responsesSnapshot.docs.slice(i, i + 450));
    }

    for (const chunk of chunks) {
      const batch = writeBatch(db);
      chunk.forEach(docSnap => {
        batch.delete(docSnap.ref);
        responsesDeleted++;
      });
      await batch.commit();
    }
  }

  // Clear surveyId reference from any workloads using this survey
  const workloadsQuery = query(
    collection(db, 'workloads'),
    where('surveyId', '==', surveyId)
  );
  const workloadsSnapshot = await getDocs(workloadsQuery);

  if (workloadsSnapshot.docs.length > 0) {
    const batch = writeBatch(db);
    workloadsSnapshot.docs.forEach(docSnap => {
      batch.update(docSnap.ref, { surveyId: null, surveyName: null });
    });
    await batch.commit();
  }

  // Delete the survey document itself
  const docRef = doc(db, COLLECTION_NAME, surveyId);
  await deleteDoc(docRef);

  return { responsesDeleted };
}


export function subscribeToSurvey(
  surveyId: string,
  callback: (survey: Survey | null) => void
): () => void {
  const docRef = doc(db, COLLECTION_NAME, surveyId);

  return onSnapshot(docRef, (snapshot) => {
    if (snapshot.exists()) {
      callback({
        id: snapshot.id,
        ...snapshot.data(),
      } as Survey);
    } else {
      callback(null);
    }
  });
}

export function subscribeToProjectSurveys(
  projectId: string,
  callback: (surveys: Survey[]) => void
): () => void {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('projectId', '==', projectId),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const surveys = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Survey[];
    callback(surveys);
  });
}


function generateQuestionId(): string {
  return `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Export service object
export const surveyService = {
  create: createSurvey,
  duplicate: duplicateSurvey,
  get: getSurvey,
  getProjectSurveys,
  getActiveSurveys,
  getAllActiveSurveys,
  update: updateSurvey,
  updateQuestions: updateSurveyQuestions,
  updateSettings: updateSurveySettings,
  updateStatus: updateSurveyStatus,
  updateConditionalLogic,
  updateSkipPatterns,
  publish: publishSurvey,
  unpublish: unpublishSurvey,
  archive: archiveSurvey,
  restore: restoreSurvey,
  addQuestion,
  updateQuestion,
  deleteQuestion,
  reorderQuestions,
  delete: deleteSurvey,
  subscribeToSurvey,
  subscribeToProjectSurveys,
};

export default surveyService;
